import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Stack,
  Tooltip,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useApiRequests } from 'hooks/useApiRequests';
import { DocumentWithMetadataDto } from 'types/document';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { Spinner } from 'components';
import { isHipaaLink } from '../../../config/productProfile';
import { FolderIcon } from 'assets/icons';

import styles from './DocumentsPickerDialog.module.scss';

type DocumentsPickerDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelectDocument: (doc: DocumentWithMetadataDto) => void;
};

export function DocumentsPickerDialog({ open, onClose, onSelectDocument }: DocumentsPickerDialogProps): JSX.Element {
  const { t } = useTranslation('link');
  const user = useAppSelector(selectUser);
  const { listClinicDocuments, listPersonalDocumentsWithMetadata } = useApiRequests();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [documents, setDocuments] = useState<DocumentWithMetadataDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<'personal' | 'clinic'>('personal');
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');

  const FILES_PER_PAGE = 10;
  const [visibleCount, setVisibleCount] = useState(FILES_PER_PAGE);

  useEffect(() => {
    if (open) setVisibleCount(FILES_PER_PAGE);
  }, [open]);

  useEffect(() => {
    if (!user?.clinicId || !user?.id) return;
    setSelectedPaths(new Set());
    setCurrentFolder(null);
    if (mode === 'clinic') {
      setCurrentPath(`Clinic/${user.clinicId}`);
    } else {
      setCurrentPath(`Personal/${user.clinicId}/${user.id}`);
    }
  }, [mode, user?.clinicId, user?.id]);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!user?.clinicId || !user?.id) return;
      setLoading(true);
      try {
        if (mode === 'personal') {
          const path = `Personal/${user.clinicId}/${user.id}`;
          const docs = await listPersonalDocumentsWithMetadata(path);
          setDocuments(docs);
        } else {
          const path = currentPath || `Clinic/${user.clinicId}`;
          const docs = await listClinicDocuments(path);
          setDocuments(docs);
        }
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [mode, user?.clinicId, user?.id, currentPath]);

  const [recentlyShared, setRecentlyShared] = useState<DocumentWithMetadataDto[]>([]);

  const fileDocuments = useMemo(() => documents.filter((d) => d.metadata !== null), [documents]);
  const clinicFolders = useMemo(() => {
    if (mode !== 'clinic' || currentFolder) return [];

    return documents
      .filter((d) => d.metadata === null && typeof d.path === 'string')
      .map((d) => d.path.replace(/\/+$/, '').split('/').pop() || '')
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [mode, currentFolder, documents]);

  useEffect(() => {
    if (!user?.clinicId || !user?.id || fileDocuments.length === 0) return;

    if (mode === 'personal') {
      const recent = [...fileDocuments]
        .filter((d) => d.metadata?.lastSharedAt)
        .sort((a, b) => new Date(b.metadata!.lastSharedAt!).getTime() - new Date(a.metadata!.lastSharedAt!).getTime())
        .slice(0, 5);

      setRecentlyShared(recent);
    } else {
      const key = `clinicRecentDocs:${user.clinicId}`;
      const recentPaths: string[] = JSON.parse(localStorage.getItem(key) ?? '[]');

      const recentDocs = recentPaths
        .map((path) => fileDocuments.find((d) => d.path === path))
        .filter(Boolean) as DocumentWithMetadataDto[];

      setRecentlyShared(recentDocs);
    }
  }, [fileDocuments, user?.clinicId, user?.id, mode]);

  const remaining = [...fileDocuments]
    .filter((doc) => !recentlyShared.includes(doc))
    .sort((a, b) => {
      const nameA = a.path.split('/').pop()?.toLowerCase() || '';
      const nameB = b.path.split('/').pop()?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

  const renderDocRow = (doc: DocumentWithMetadataDto) => {
    const name = doc.path.split('/').pop() || '';
    const size = doc.metadata?.size ?? 0;
    const sizeLabel = size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${(size / 1024).toFixed(1)} KB`;
    const isSelected = selectedPaths.has(doc.path);

    const toggleSelection = () => {
      setSelectedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(doc.path)) {
          next.delete(doc.path);
        } else {
          next.add(doc.path);
        }
        return next;
      });
    };

    return (
      <Grid
        container
        spacing={0}
        alignItems='center'
        wrap='nowrap'
        key={doc.path}
        onClick={toggleSelection}
        sx={{
          cursor: 'pointer',
          backgroundColor: isSelected ? (isDarkMode ? 'rgba(129, 207, 221, 0.22)' : '#E3F2FD') : 'transparent',
          '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#F5F5F5' },
          borderRadius: '0.4rem',
          px: 1,
          py: '4px',
          transition: 'background-color 0.2s',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Grid
          item
          xs={8.4}
          md={9.6}
          sx={{
            pl: '10px',
            pr: '4px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '32px',
            height: '100%',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Tooltip title={name} disableInteractive enterDelay={500}>
            <Typography variant='body1' noWrap>
              {name}
            </Typography>
          </Tooltip>
        </Grid>

        <Grid
          item
          xs={3.6}
          md={2.4}
          sx={{
            pr: '6px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: '32px',
            height: '100%',
            minWidth: 0,
          }}
        >
          <Typography variant='body1' align='right' noWrap>
            {sizeLabel}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const sendSelectedFiles = (): void => {
    const selectedDocs = fileDocuments.filter((doc) => selectedPaths.has(doc.path));
    selectedDocs.forEach((doc) => onSelectDocument(doc));
    onClose();
  };

  const openFolder = (folderName: string) => {
    if (!user?.clinicId) return;
    setCurrentFolder(folderName);
    setCurrentPath(`Clinic/${user.clinicId}/${folderName}`);
    setSelectedPaths(new Set());
  };

  const backToSharedRoot = () => {
    if (!user?.clinicId) return;
    setCurrentFolder(null);
    setCurrentPath(`Clinic/${user.clinicId}`);
    setSelectedPaths(new Set());
  };

  return (
    <Dialog className={styles.dialog} classes={{ paper: styles.dialogPaper }} open={open} onClose={onClose}>
      <div className={styles.header}>
        <Typography variant='body4'>{t('documentsPicker.selectFiles')}</Typography>
        <CloseIcon className={styles.closeIcon} color='primary' onClick={onClose} />
      </div>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ mb: 2, pr: 2 }}>
          <Select
            value={mode}
            size='small'
            onChange={(e: SelectChangeEvent) => setMode(e.target.value as 'personal' | 'clinic')}
            fullWidth
            sx={{ pl: 0 }}
          >
            <MenuItem value='personal'>{t('documentsPicker.myDocuments')}</MenuItem>
            <MenuItem value='clinic'>
              {isHipaaLink ? t('documentsPicker.clinicDocuments') : t('documentsPicker.sharedDocuments')}
            </MenuItem>
          </Select>
        </Box>

        {loading && (
          <Stack alignItems='center' sx={{ mt: 2 }}>
            <Spinner small />
          </Stack>
        )}

        {!loading &&
          (fileDocuments.length > 0 || (mode === 'clinic' && !currentFolder && clinicFolders.length > 0)) && (
            <Box
              sx={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.18)' : '#DFDFDF'}`,
                borderRadius: '0.6rem',
                ml: 0,
                mr: 2,
                px: 1,
                pt: 1,
                pb: 2,
                mb: 1,
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
              }}
            >
              {mode === 'clinic' && currentFolder && (
                <Box
                  onClick={backToSharedRoot}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    mb: 1,
                    borderRadius: '0.4rem',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#F5F5F5' },
                  }}
                >
                  <FolderIcon sx={{ width: 20, height: 20, color: '#FBC02D' }} />
                  <Typography variant='body1'>...</Typography>
                </Box>
              )}

              {mode === 'clinic' && !currentFolder && clinicFolders.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ pl: 1, pr: 1, mt: 1 }}>
                    {t('documentsPicker.folders')}
                  </Typography>
                  <Box
                    sx={{
                      height: '0.1rem',
                      background: isDarkMode ? 'rgba(255,255,255,0.18)' : '#DFDFDF',
                      mb: 1,
                      mx: 1,
                    }}
                  />
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {clinicFolders.map((folderName) => (
                      <Box
                        key={folderName}
                        onClick={() => openFolder(folderName)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderRadius: '0.4rem',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#F5F5F5' },
                        }}
                      >
                        <FolderIcon sx={{ width: 20, height: 20, color: '#FBC02D' }} />
                        <Typography variant='body1' noWrap>
                          {folderName}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              {(!currentFolder || mode === 'personal') && recentlyShared.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ pl: 1, pr: 1, mt: 1 }}>
                    {t('documentsPicker.recentlyShared')}
                  </Typography>
                  <Box
                    sx={{
                      height: '0.1rem',
                      background: isDarkMode ? 'rgba(255,255,255,0.18)' : '#DFDFDF',
                      mb: 1,
                      mx: 1,
                    }}
                  />
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {recentlyShared.map(renderDocRow)}
                  </Stack>
                </>
              )}

              {remaining.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ pl: 1, pr: 1 }}>
                    {t('documentsPicker.allFiles')}
                  </Typography>
                  <Box
                    sx={{
                      height: '0.1rem',
                      background: isDarkMode ? 'rgba(255,255,255,0.18)' : '#DFDFDF',
                      mb: 1,
                      mx: 1,
                    }}
                  />

                  <Stack spacing={1}>
                    {remaining.slice(0, visibleCount).map(renderDocRow)}

                    {remaining.length > visibleCount && (
                      <Button
                        onClick={() => setVisibleCount((prev) => prev + FILES_PER_PAGE)}
                        size='small'
                        variant='text'
                        sx={{
                          mt: 1,
                          alignSelf: 'center',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1.2rem',
                          color: 'var(--app-brand-primary)',
                        }}
                      >
                        {t('documentsPicker.showMore')}
                      </Button>
                    )}
                  </Stack>
                </>
              )}
            </Box>
          )}

        {!loading &&
          fileDocuments.length === 0 &&
          !(mode === 'clinic' && !currentFolder && clinicFolders.length > 0) && (
            <Typography variant='body2' sx={{ mt: 2, px: 2, color: 'var(--app-text-muted)' }}>
              {t('documentsPicker.noDocumentsAvailable')}
            </Typography>
          )}
      </DialogContent>

      <DialogActions sx={{ pb: 0 }}>
        <Button variant='outlined' onClick={onClose}>
          {t('documentsPicker.cancel')}
        </Button>
        <Button variant='contained' onClick={sendSelectedFiles} disabled={selectedPaths.size === 0}>
          {t('documentsPicker.sendFiles')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
